import { apiClient } from "./authService";
import { peekCached } from "./apiCache";
import { refreshProductCache, rememberCatalog, searchLocalProducts } from "../offline/productCache";

function productsPageUrl({ page = 1, pageSize = 50, search = "", category = "", subcategory = "", laboratoire = "", stockState = "" } = {}) {
  const params = new URLSearchParams();
  params.set("page", page);
  params.set("page_size", pageSize);
  if (search) params.set("search", search);
  if (category) params.set("category", category);
  if (subcategory) params.set("subcategory", subcategory);
  if (laboratoire) params.set("laboratoire", laboratoire);
  if (stockState) params.set("stock_state", stockState);
  return `products/?${params.toString()}`;
}

const productService = {
  // Last known page (even outdated) for painting the list at once, or null.
  peekProductsPage: (opts = {}) => {
    const data = peekCached(productsPageUrl(opts));
    if (!data) return null;
    if (Array.isArray(data)) return { results: data, count: data.length };
    return { results: data.results || [], count: data.count || 0 };
  },

  // Full list — used by sales/facturation selectors. Single roundtrip via FlexiblePagination.
  getAllProducts: async () => {
    try {
      const response = await apiClient.get("products/?page_size=5000");
      const data = response.data;
      const list = Array.isArray(data) ? data : (data?.results || []);
      rememberCatalog(list);
      return list;
    } catch (error) {
      console.error("Error fetching products:", error);
      throw error.response?.data || { message: "Error fetching products" };
    }
  },

  // Server-side paginated list — used by ProductList UI.
  // Returns { results, count } so the caller can render page controls.
  getProductsPage: async ({ page = 1, pageSize = 50, search = "", category = "", subcategory = "", laboratoire = "", stockState = "" } = {}) => {
    try {
      const response = await apiClient.get(productsPageUrl({ page, pageSize, search, category, subcategory, laboratoire, stockState }));
      const data = response.data;
      // We're online: quietly keep the on-device catalog fresh so offline search
      // (below) has an up-to-date copy to work from. Throttled; never blocks.
      refreshProductCache();
      if (Array.isArray(data)) return { results: data, count: data.length };
      return { results: data?.results || [], count: data?.count || 0 };
    } catch (error) {
      // No internet (network error, no server response): search the locally
      // cached catalog so the picker still works offline. A real server
      // rejection (has a response) is still surfaced as an error.
      if (!error.response) {
        return searchLocalProducts({ search, page, pageSize });
      }
      console.error("Error fetching products page:", error);
      throw error.response?.data || { message: "Error fetching products" };
    }
  },

  getProductById: async (id) => {
    try {
      const response = await apiClient.get(`products/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching product ${id}:`, error);
      throw error.response?.data || { message: "Error fetching product details" };
    }
  },

  // Historique de vente for one product (SaleDetail lines + totals).
  getSalesHistory: async (id, { startDate = "", endDate = "" } = {}) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      const qs = params.toString();
      const response = await apiClient.get(`products/${id}/sales-history/${qs ? `?${qs}` : ""}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching sales history for product ${id}:`, error);
      throw error.response?.data || { message: "Error fetching sales history" };
    }
  },

  // Historique d'achat for one product (FactureDetail lines + totals).
  getPurchaseHistory: async (id, { startDate = "", endDate = "" } = {}) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      const qs = params.toString();
      const response = await apiClient.get(`products/${id}/purchase-history/${qs ? `?${qs}` : ""}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching purchase history for product ${id}:`, error);
      throw error.response?.data || { message: "Error fetching purchase history" };
    }
  },

  createProduct: async (productData) => {
    try {
      const response = await apiClient.post("products/", productData);
      return response.data;
    } catch (error) {
      console.error("Error creating product:", error);
      throw error.response?.data || { message: "Error creating product" };
    }
  },

  // Mise à jour partielle (ex: prix d'achat / prix de vente seulement).
  patchProduct: async (id, data) => {
    try {
      const response = await apiClient.patch(`products/${id}/`, data);
      return response.data;
    } catch (error) {
      console.error(`Error patching product ${id}:`, error);
      throw error.response?.data || { message: "Error updating product" };
    }
  },

  updateProduct: async (id, productData) => {
    try {
      const response = await apiClient.put(`products/${id}/`, productData);
      return response.data;
    } catch (error) {
      console.error(`Error updating product ${id}:`, error);
      throw error.response?.data || { message: "Error updating product" };
    }
  },

  // Multipart variants — used when a product image file is uploaded.
  createProductForm: async (formData) => {
    try {
      const response = await apiClient.post("products/", formData, { headers: { "Content-Type": "multipart/form-data" } });
      return response.data;
    } catch (error) {
      console.error("Error creating product (multipart):", error);
      throw error.response?.data || { message: "Error creating product" };
    }
  },

  updateProductForm: async (id, formData) => {
    try {
      const response = await apiClient.put(`products/${id}/`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      return response.data;
    } catch (error) {
      console.error(`Error updating product (multipart) ${id}:`, error);
      throw error.response?.data || { message: "Error updating product" };
    }
  },

  // Variante partielle du multipart : la fiche produit n'envoie que les champs
  // modifiés + la photo, pour ne pas réécrire le stock avec une valeur périmée.
  patchProductForm: async (id, formData) => {
    try {
      const response = await apiClient.patch(`products/${id}/`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      return response.data;
    } catch (error) {
      console.error(`Error patching product (multipart) ${id}:`, error);
      throw error.response?.data || { message: "Error updating product" };
    }
  },

  deleteProduct: async (id) => {
    try {
      const response = await apiClient.delete(`products/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting product ${id}:`, error);
      throw error.response?.data || { message: "Error deleting product" };
    }
  },

  getCategories: async () => {
    try {
      const response = await apiClient.get("categories/?page_size=5000");
      const data = response.data;
      if (Array.isArray(data)) return data;
      return data?.results || [];
    } catch (error) {
      console.error("Error fetching categories:", error);
      throw error.response?.data || {
        message: `Error fetching product categories: ${error.message || "Unknown error"}`,
      };
    }
  },

  createCategory: async (categoryData) => {
    try {
      const response = await apiClient.post("categories/", categoryData);
      return response.data;
    } catch (error) {
      console.error("Error creating category:", error);
      throw error.response?.data || { message: "Error creating category" };
    }
  },

  updateCategory: async (id, categoryData) => {
    try {
      const response = await apiClient.put(`categories/${id}/`, categoryData);
      return response.data;
    } catch (error) {
      console.error(`Error updating category ${id}:`, error);
      throw error.response?.data || { message: "Error updating category" };
    }
  },

  deleteCategory: async (id) => {
    try {
      const response = await apiClient.delete(`categories/${id}/`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting category ${id}:`, error);
      throw error.response?.data || { message: "Error deleting category" };
    }
  },
};

export default productService;
